import { TextTooltip } from '@components'
import Color from 'color'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useCallback, useRef } from 'react'
import { FieldErrors } from 'react-hook-form'
import { FaExclamationCircle, FaQuestionCircle } from 'react-icons/fa'
import styled from 'styled-components'

export interface RadioOption {
  value: string | number
  label: string
  description?: string
  tooltip?: string
}

interface Props {
  options: RadioOption[]
  value?: string | number
  onChange: (value: string | number) => void
  errors?: FieldErrors
  name: string
  disabled?: boolean
  subtle?: boolean
}

export const RadioGroup = React.forwardRef<HTMLDivElement, Props>(
  ({ options, value, onChange, errors, name, disabled = false, subtle = false }, ref) => {
    const itemRefs = useRef<(HTMLDivElement | null)[]>([])
    const selectedIndex = options.findIndex(option => option.value === value)
    const focusIndex = selectedIndex >= 0 ? selectedIndex : 0

    const selectByOffset = useCallback(
      (offset: number) => {
        if (disabled || options.length === 0) return
        const current = selectedIndex >= 0 ? selectedIndex : 0
        const next = (current + offset + options.length) % options.length
        onChange(options[next].value)
        itemRefs.current[next]?.focus()
      },
      [disabled, onChange, options, selectedIndex]
    )

    return (
      <StyledRadioGroup role="radiogroup" ref={ref}>
        {options.map((option, index) => {
          const isSelected = value === option.value
          return (
            <RadioItem
              key={option.value}
              ref={el => {
                itemRefs.current[index] = el
              }}
              $isSelected={isSelected}
              $disabled={disabled}
              role="radio"
              aria-checked={isSelected}
              tabIndex={disabled ? -1 : index === focusIndex ? 0 : -1}
              onClick={e => {
                e.preventDefault()
                if (!disabled) {
                  onChange(option.value)
                }
              }}
              onKeyDown={e => {
                if (disabled) return
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onChange(option.value)
                  return
                }
                if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                  e.preventDefault()
                  selectByOffset(1)
                  return
                }
                if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                  e.preventDefault()
                  selectByOffset(-1)
                }
              }}
            >
              <RadioDot $isSelected={isSelected} aria-hidden />
              <RadioContent>
                <RadioLabelRow>
                  <RadioLabel $subtle={subtle}>{option.label}</RadioLabel>
                  {option.tooltip && (
                    <TextTooltip tooltip={option.tooltip} placement="bottom-start">
                      <RadioTooltipIcon>
                        <FaQuestionCircle size={14} />
                      </RadioTooltipIcon>
                    </TextTooltip>
                  )}
                </RadioLabelRow>
                <AnimatePresence initial={false}>
                  {isSelected && option.description && (
                    <RadioDescription
                      key="description"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {option.description}
                    </RadioDescription>
                  )}
                </AnimatePresence>
              </RadioContent>
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                style={{ display: 'none' }}
                disabled={disabled}
                tabIndex={-1}
              />
            </RadioItem>
          )
        })}
        {errors?.[name] && (
          <StyledError>
            <FaExclamationCircle />
            {errors[name]?.message?.toString()}
          </StyledError>
        )}
      </StyledRadioGroup>
    )
  }
)

RadioGroup.displayName = 'RadioGroup'

const StyledRadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const RadioItem = styled.div<{ $isSelected: boolean; $disabled: boolean }>`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 0.6rem;
  background: ${({ theme, $isSelected }) =>
    $isSelected ? Color(theme.colors.primary).alpha(0.06).string() : 'transparent'};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};

  &:hover {
    background: ${({ theme, $isSelected, $disabled }) =>
      $disabled
        ? $isSelected
          ? Color(theme.colors.primary).alpha(0.06).string()
          : 'transparent'
        : $isSelected
        ? Color(theme.colors.primary).alpha(0.06).string()
        : theme.colors.defaultHover};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`

const RadioDot = styled.span<{ $isSelected: boolean }>`
  width: 16px;
  height: 16px;
  margin-top: 2px;
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: 50%;
  border: ${({ theme, $isSelected }) =>
    $isSelected ? `5px solid ${theme.colors.primary}` : `1.5px solid ${theme.colors.borderStrong}`};
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const RadioContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const RadioLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const RadioLabel = styled.div<{ $subtle: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  font-weight: ${({ $subtle }) => ($subtle ? 400 : 600)};
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.neutralBlack};
`

const RadioTooltipIcon = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.metalGray};
`

const RadioDescription = styled(motion.div)`
  overflow: hidden;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: 1.45;
`

const StyledError = styled.div`
  color: ${({ theme }) => theme.colors.error};
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
`
